import { FormArray, FormControl, FormGroup } from '@angular/forms';
import {
  AppearanceMode,
  ElevationConfig,
  ElevationLevel,
  ElevationModeOverride,
} from '@finxone-platform/shared/sys-config-types';
import { renameFormGroupControl } from './global-theme-form-group-reorder.utils';

/*
 * `elevation` is a root-level theme control that only exists once a theme has elevation
 * (on load, or when the first level is added), so a theme without it saves exactly as
 * before. Levels are a FormArray to keep their order; mode overrides are sparse and keyed
 * by level name, so reordering never touches them.
 */

export const DEFAULT_SHADOW_COLOUR = '#0F172A';

export const FIRST_ELEVATION_LEVEL: Omit<ElevationLevel, 'name'> = {
  x: '0px',
  y: '1px',
  blur: '2px',
  spread: '0px',
  opacity: 8,
};

export function buildElevationLevelFormGroup(level: ElevationLevel): FormGroup {
  return new FormGroup({
    name: new FormControl(level.name),
    x: new FormControl(level.x),
    y: new FormControl(level.y),
    blur: new FormControl(level.blur),
    spread: new FormControl(level.spread),
    opacity: new FormControl(level.opacity),
  });
}

export function buildElevationFormGroup(elevation: ElevationConfig): FormGroup {
  return new FormGroup({
    shadowColor: new FormControl(elevation.shadowColor),
    levels: new FormArray((elevation.levels ?? []).map(buildElevationLevelFormGroup)),
  });
}

export function buildElevationOverrideFormGroup(override: ElevationModeOverride): FormGroup {
  const group = new FormGroup<Record<string, FormControl | FormGroup>>({});
  if (override.shadowColor) group.addControl('shadowColor', new FormControl(override.shadowColor));
  if (override.levels) {
    const levels: Record<string, FormGroup> = {};
    for (const [name, level] of Object.entries(override.levels)) {
      levels[name] = new FormGroup({ opacity: new FormControl(level?.opacity) });
    }
    group.addControl('levels', new FormGroup(levels));
  }
  return group;
}

export function getElevationLevelsFormArray(themeFormGroup: FormGroup): FormArray<FormGroup> | null {
  return themeFormGroup.get(['elevation', 'levels']) as FormArray<FormGroup> | null;
}

/** Override levels groups across every mode, enabled or not. */
function overrideLevelGroups(themeFormGroup: FormGroup): FormGroup[] {
  const modeOverrides = themeFormGroup.get('modeOverrides') as FormGroup | null;
  if (!modeOverrides) return [];
  return Object.keys(modeOverrides.controls)
    .map((mode) => modeOverrides.get([mode, 'elevation', 'levels']) as FormGroup | null)
    .filter((levels): levels is FormGroup => !!levels);
}

export function addElevationLevel(themeFormGroup: FormGroup, level: ElevationLevel): void {
  if (!themeFormGroup.get('elevation')) {
    themeFormGroup.addControl(
      'elevation',
      buildElevationFormGroup({ shadowColor: DEFAULT_SHADOW_COLOUR, levels: [level] }),
    );
    return;
  }
  getElevationLevelsFormArray(themeFormGroup)?.push(buildElevationLevelFormGroup(level));
}

/** Renames a level and its key in every mode's overrides. */
export function renameElevationLevel(themeFormGroup: FormGroup, index: number, newName: string): void {
  const nameControl = getElevationLevelsFormArray(themeFormGroup)?.at(index)?.get('name');
  if (!nameControl) return;
  const oldName: string = nameControl.value;
  if (oldName === newName) return;
  for (const levels of overrideLevelGroups(themeFormGroup)) {
    if (levels.get(oldName)) renameFormGroupControl(levels, oldName, newName);
  }
  nameControl.setValue(newName);
}

/** Removes a level and its entries in every mode's overrides. */
export function removeElevationLevel(themeFormGroup: FormGroup, index: number): void {
  const levelsArray = getElevationLevelsFormArray(themeFormGroup);
  const name: string | undefined = levelsArray?.at(index)?.get('name')?.value;
  if (!levelsArray || name == null) return;
  for (const levels of overrideLevelGroups(themeFormGroup)) levels.removeControl(name, { emitEvent: false });
  levelsArray.removeAt(index);
}

export function reorderElevationLevel(themeFormGroup: FormGroup, fromIndex: number, toIndex: number): void {
  const levelsArray = getElevationLevelsFormArray(themeFormGroup);
  if (!levelsArray || fromIndex === toIndex) return;
  if (fromIndex < 0 || toIndex < 0 || fromIndex >= levelsArray.length || toIndex >= levelsArray.length) return;
  const control = levelsArray.at(fromIndex);
  levelsArray.removeAt(fromIndex, { emitEvent: false });
  levelsArray.insert(toIndex, control);
}

/** The mode's elevation override group, created on demand; null for the base mode or without an appearance. */
function modeElevationGroup(themeFormGroup: FormGroup, mode: AppearanceMode, create: boolean): FormGroup | null {
  const modeOverrides = themeFormGroup.get('modeOverrides') as FormGroup | null;
  if (!modeOverrides || themeFormGroup.get('appearance.baseMode')?.value === mode) return null;
  const existing = modeOverrides.get([mode, 'elevation']) as FormGroup | null;
  if (existing || !create) return existing;

  if (!modeOverrides.get(mode)) modeOverrides.addControl(mode, new FormGroup({}), { emitEvent: false });
  const modeGroup = modeOverrides.get(mode) as FormGroup;
  modeGroup.addControl('elevation', new FormGroup({}), { emitEvent: false });
  return modeGroup.get('elevation') as FormGroup;
}

/** Sets a mode's shadow colour; an empty value removes the override. */
export function setShadowColourOverride(themeFormGroup: FormGroup, mode: AppearanceMode, value: string): void {
  const group = modeElevationGroup(themeFormGroup, mode, !!value);
  if (!group) return;
  const control = group.get('shadowColor');
  if (!value) group.removeControl('shadowColor');
  else if (control) control.setValue(value);
  else group.addControl('shadowColor', new FormControl(value));
}

/** Sets a level's opacity in a mode; null removes the override so the level inherits the base value. */
export function setOpacityOverride(
  themeFormGroup: FormGroup,
  mode: AppearanceMode,
  levelName: string,
  opacity: number | null,
): void {
  const group = modeElevationGroup(themeFormGroup, mode, opacity != null);
  if (!group) return;
  if (opacity == null) {
    (group.get('levels') as FormGroup | null)?.removeControl(levelName);
    return;
  }
  if (!group.get('levels')) group.addControl('levels', new FormGroup({}), { emitEvent: false });
  const levels = group.get('levels') as FormGroup;
  const control = levels.get([levelName, 'opacity']);
  if (control) control.setValue(opacity);
  else levels.addControl(levelName, new FormGroup({ opacity: new FormControl(opacity) }));
}
